import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import type { DashboardSummaryResponse } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * Billed by month — the ONE chart §11.2 allows, per its 2026-08-14 amendment.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS "BILLED", NOT "REVENUE", AND THAT IS NOT PEDANTRY. This app has no idea
 * who has paid — payment tracking is §17.5 and does not exist. §11.2's known gap
 * forbids the home screen implying payment status at all, so calling this
 * "revenue" or "earnings" would put a claim on the first screen the owner opens
 * that the software cannot back up. The API field is `totalBilledPaise` for the
 * same reason, and an API test fails on the words revenue/income/earnings.
 *
 * EVERY MONTH IS TAPPABLE, and that is WHY the chart is allowed at all. §11.2
 * bans charts because "if a number cannot be acted on, it does not belong on
 * this screen" — so each column opens that month's bills, the same gesture the
 * at-a-glance cards offer, extended to the other eleven months. Take the links
 * away and this becomes exactly the decoration the section rules out.
 *
 * APRIL TO MARCH, never a rolling twelve months. It is the year the owner's CA
 * works in, the year his invoice numbers reset in (§6, `26-27/67`), and the year
 * the `?fy=` parameter selects. A rolling window would put a boundary in the
 * middle of it and make the shape disagree with every other total in the app.
 *
 * THE ZEROS ARE PLOTTED, NOT FILTERED OUT. A month with no business is a zero;
 * dropping it would let the line run straight from April to June and draw a May
 * that never happened. Months that have not happened yet are zeros too — the
 * series always has twelve entries.
 *
 * NO TIME-RANGE TOGGLE. The block ships one (7d / 30d / 90d); those windows have
 * no meaning here, and the financial year is not a preference. §11.2 wants fewer
 * controls above the actions, not more.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Recharts needs one flat row per column. */
interface Column {
  /** `2026-04-01` — the API's inclusive `from`, and the navigation key. */
  from: string
  to: string
  label: string
  billed: number
  invoiceCount: number
}

export function ChartBilledByMonth({
  summary,
  loading,
  /**
   * The gutter the card sits in. Home is a full-bleed column whose children
   * carry their own horizontal padding, so that is the default; Reports (§11.2's
   * secondary screen, which shows this same chart for the period's financial
   * year) is a padded page and passes `""` so the card is not inset twice.
   *
   * A PROP RATHER THAN A COPY OF THE COMPONENT. Two charts drawing the same
   * series would be two places for "billed, never revenue" and the twelve
   * tappable months to drift apart — and the second copy is always the one that
   * misses the next fix.
   */
  className = "px-4 lg:px-6",
}: {
  summary: DashboardSummaryResponse | undefined
  loading: boolean
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const columns = useMemo<Column[]>(() => {
    /*
     * `?? []` GUARDS A DEPLOYMENT ORDER, not a type hole. The API and the SPA
     * ship separately; between the two there is a window where a browser holding
     * the new bundle talks to the old API, which has no `monthlyBilled` at all.
     * Without this the home screen — the FIRST screen, the one the owner opens
     * from the workshop — throws on `.map` of undefined and renders white.
     * The card removes itself instead (see the empty check below).
     */
    if (summary === undefined) return []

    return (summary.monthlyBilled ?? []).map((month) => ({
      from: month.from,
      to: month.to,
      /*
       * "Apr", "May" … in the active language. `from` is a plain `YYYY-MM-DD`
       * calendar day, not an instant — parsing it with `new Date(...)` would
       * read it as UTC MIDNIGHT and render the month before in any timezone west
       * of Greenwich. Splitting the string keeps the day the server meant, and
       * `Date.UTC` + `timeZone: "UTC"` formats it without a second conversion.
       */
      label: monthLabel(month.from, i18n.language),
      /*
       * PAISE, the integer the server sent, carried all the way to the
       * formatter. Recharts only ever compares these against each other to
       * scale the plot, so the magnitude costs nothing — and dividing by 100
       * here is the exact moment money becomes a float (CLAUDE.md). There is no
       * Y axis to label, so nothing needs the rupee value but the tooltip, and
       * that goes through `formatRupees`.
       */
      billed: month.totalBilledPaise,
      invoiceCount: month.invoiceCount,
    }))
  }, [summary, i18n.language])

  const chartConfig = {
    billed: { label: t("home.chart.billed"), color: "var(--primary)" },
  } satisfies ChartConfig

  const openMonth = (column: Column | undefined) => {
    // A month with nothing in it has nothing to open. Navigating to an empty
    // register would be a dead end the owner has to back out of.
    if (column === undefined || column.invoiceCount === 0) return
    navigate(routes.invoicesInPeriod(column.from, column.to))
  }

  // Loaded, but the series is not there — an API older than this bundle. Show
  // nothing rather than an empty chart frame; the rest of the home screen is
  // unaffected and still does its job.
  if (!loading && summary !== undefined && columns.length === 0) return null

  return (
    <div className={className}>
      <Card className="@container/card">
        <CardHeader>
          <CardTitle>{t("home.chart.title")}</CardTitle>
          <CardDescription>
            <span className="hidden @[540px]/card:block">
              {t("home.chart.subtitle", { fy: summary?.financialYear.label ?? "" })}
            </span>
            <span className="@[540px]/card:hidden">{summary?.financialYear.label ?? ""}</span>
          </CardDescription>
          {summary === undefined ? null : (
            <CardAction className="text-muted-foreground text-sm tabular-nums">
              {/* The year's total, summed from the same columns the chart plots
                * so the two can never disagree. Integer paise throughout — the
                * addition happens before any formatting. */}
              {formatRupees(columns.reduce((sum, column) => sum + column.billed, 0))}
            </CardAction>
          )}
        </CardHeader>

        <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
          {loading || summary === undefined ? (
            <Skeleton className="h-[250px] w-full" />
          ) : (
            <>
              <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
                <AreaChart
                  data={columns}
                  margin={{ left: 4, right: 4 }}
                  onClick={(state) => {
                    // Recharts reports the index of the column under the
                    // pointer; anywhere on the plot counts, which is a much
                    // larger target than the line itself — §11.1 wants big
                    // targets, and the owner is using this on a phone.
                    const index = state?.activeTooltipIndex
                    if (typeof index === "number") openMonth(columns[index])
                  }}
                  className="cursor-pointer"
                >
                  <defs>
                    <linearGradient id="fillBilled" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-billed)" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="var(--color-billed)" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid vertical={false} />

                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval="preserveStartEnd"
                  />

                  {/*
                    * NO Y AXIS, as in shadcn's own area chart. A column of
                    * "₹12,77,645" up the side is four repetitions of a number
                    * the owner did not ask for, and abbreviating them to "12.8L"
                    * would mean a rupee formatter outside `packages/tax` —
                    * exactly the second formatter CLAUDE.md exists to prevent.
                    * The tooltip carries the exact figure, and the card header
                    * carries the year's total.
                    */}

                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        labelFormatter={(_label, payload) => {
                          const column = payload?.[0]?.payload as Column | undefined
                          if (column === undefined) return ""
                          return `${column.label} · ${t("home.card.bills", {
                            count: column.invoiceCount,
                          })}`
                        }}
                        formatter={(value) => (
                          <span className="font-medium tabular-nums">
                            {formatRupees(Number(value))}
                          </span>
                        )}
                      />
                    }
                  />

                  <Area
                    dataKey="billed"
                    type="natural"
                    fill="url(#fillBilled)"
                    stroke="var(--color-billed)"
                  />
                </AreaChart>
              </ChartContainer>

              {/*
                * THE CHART IS NOT REACHABLE BY KEYBOARD OR SCREEN READER, and an
                * SVG path cannot be made so honestly. This row is the same
                * twelve destinations as real buttons — it is not a fallback but
                * the accessible primary, and it doubles as the month picker on a
                * phone where tapping a 20px-wide slice of an area chart is a
                * coin toss (§11.1 — 48px targets, and the owner is 55).
                */}
              <div
                className="mt-4 grid grid-cols-6 gap-1 @[560px]/card:grid-cols-12"
                aria-label={t("home.chart.monthsLabel")}
              >
                {columns.map((column) => (
                  <button
                    key={column.from}
                    type="button"
                    disabled={column.invoiceCount === 0}
                    onClick={() => openMonth(column)}
                    className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring rounded-md px-1 py-1.5 text-xs tabular-nums transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40"
                    title={t("home.chart.openMonth", {
                      month: column.label,
                      amount: formatRupees(column.billed),
                    })}
                  >
                    {column.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * `2026-04-01` → `Apr`, in the active language.
 *
 * NOT `new Date("2026-04-01")`. That parses as UTC midnight, so anywhere west of
 * Greenwich it renders as 31 March and the column reads "Mar". The API sends a
 * calendar day in Asia/Kolkata, deliberately without a time — this reads it as
 * one.
 */
function monthLabel(isoDay: string, language: string): string {
  const [year, month] = isoDay.split("-").map(Number)
  if (year === undefined || month === undefined) return isoDay

  return new Intl.DateTimeFormat(language, { month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  )
}
